import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../auth/authSlice';
import ClienteList from './ClienteList';
import ClienteForm from './ClienteForm';
import ClienteImportModal from './ClienteImportModal';
import { useBorrarCamposTextoMutation } from '../api/clientesApiSlice';
import { useGetEmpresaByIdQuery } from '../../empresas/api/empresasApiSlice';

const ClientePage: React.FC = () => {
  const currentUser = useSelector(selectCurrentUser);
  const empresaId = currentUser?.empresa_id;
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBorrarCampos, setShowBorrarCampos] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [borrarCampos] = useBorrarCamposTextoMutation();

  if (!empresaId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="bg-card p-8 rounded-lg shadow-md border border-border max-w-md w-full text-center">
          <h2 className="text-2xl font-bold mb-4 text-destructive">Acceso restringido</h2>
          <p className="mb-2">No tienes una empresa asignada.</p>
          <p>Contacta a un administrador para que te asigne una empresa antes de acceder a la gestión de clientes.</p>
        </div>
      </div>
    );
  }

  const handleDelete = async (selectedFields: string[]) => {
    if (!empresaId) return;

    try {
      const result = await borrarCampos({
        empresa_id: empresaId,
        campos: selectedFields
      }).unwrap();
      
      // Mostrar mensaje de éxito
      setSuccessMessage(`Se borraron los campos seleccionados de ${result.count} clientes.`);
      setShowBorrarCampos(false);
      
      // Ocultar el mensaje después de 5 segundos
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      console.error('Error al borrar campos:', error);
      setSuccessMessage('Hubo un error al borrar los campos. Por favor, inténtelo más tarde.');
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground py-8">
      <div className="container mx-auto">
        <div className="flex items-center justify-between mb-8 max-w-5xl mx-auto gap-4">
          <h1 className="text-3xl font-bold text-foreground">Gestión de Clientes</h1>
          <div className="flex gap-2">
            <button
              className="bg-primary text-primary-foreground px-6 py-2 rounded-md font-semibold hover:bg-primary/90 transition shadow-md"
              onClick={() => setShowForm(true)}
            >
              + Nuevo Cliente
            </button>
            <button
              className="bg-accent text-accent-foreground px-6 py-2 rounded-md font-semibold hover:bg-accent/80 transition shadow-md border border-border"
              onClick={() => setShowImport(true)}
            >
              Importar clientes
            </button>
            <button
              className="bg-yellow-500 text-white px-6 py-2 rounded-md font-semibold hover:bg-yellow-600 transition shadow-md"
              onClick={() => setShowBorrarCampos(true)}
            >
              Borrar campos
            </button>
          </div>
        </div>
        
        {/* Mensaje de éxito */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4 max-w-5xl mx-auto">
            <span className="block sm:inline">{successMessage}</span>
            <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setSuccessMessage('')}>
              <svg className="fill-current h-6 w-6 text-green-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><title>Cerrar</title><path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/></svg>
            </span>
          </div>
        )}
        
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-card dark:bg-[#181A20] text-card-foreground dark:text-white rounded-lg shadow-lg max-w-xl w-full p-8 relative border border-border max-h-[90vh] overflow-y-auto">
              <button
                className="absolute top-4 right-4 text-xl text-muted-foreground hover:text-destructive"
                onClick={() => setShowForm(false)}
                aria-label="Cerrar"
              >
                ×
              </button>
              <ClienteForm />
            </div>
          </div>
        )}
        {showImport && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-card dark:bg-[#181A20] text-card-foreground dark:text-white rounded-lg shadow-lg max-w-2xl w-full p-8 relative border border-border max-h-[90vh] overflow-y-auto">
              <button
                className="absolute top-4 right-4 text-xl text-muted-foreground hover:text-destructive"
                onClick={() => setShowImport(false)}
                aria-label="Cerrar"
              >
                ×
              </button>
              <ClienteImportModal onClose={() => setShowImport(false)} />
            </div>
          </div>
        )}
        
        {/* Modal para borrar campos */}
        {showBorrarCampos && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-card dark:bg-[#181A20] text-card-foreground dark:text-white rounded-lg shadow-lg max-w-md w-full p-8 relative border border-border">
              <button
                className="absolute top-4 right-4 text-xl text-muted-foreground hover:text-destructive"
                onClick={() => setShowBorrarCampos(false)}
                aria-label="Cerrar"
              >
                ×
              </button>
              <h2 className="text-xl font-bold mb-4">Borrar campos de texto</h2>
              <p className="mb-4 text-muted-foreground">Selecciona los campos que deseas borrar de todos los clientes.</p>
              
              <BorrarCamposForm onDelete={handleDelete} onCancel={() => setShowBorrarCampos(false)} />
            </div>
          </div>
        )}
        
        <ClienteList empresaId={empresaId} />
      </div>
    </div>
  );
};

// Componente para seleccionar campos a borrar
interface BorrarCamposFormProps {
  onDelete: (selectedFields: string[]) => void;
  onCancel: () => void;
}

const BorrarCamposForm: React.FC<BorrarCamposFormProps> = ({ onDelete, onCancel }) => {
  const currentUser = useSelector(selectCurrentUser);
  const empresaId = currentUser?.empresa_id;
  const { data: empresa } = useGetEmpresaByIdQuery(empresaId ? empresaId.toString() : '', { 
    skip: !empresaId 
  });
  
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [showConfirmacion, setShowConfirmacion] = useState(false);
  
  const toggleField = (field: string) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter(f => f !== field));
    } else {
      setSelectedFields([...selectedFields, field]);
    }
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFields.length === 0) return;
    // Mostrar pantalla de confirmación en lugar de eliminar directamente
    setShowConfirmacion(true);
  };
  
  const handleConfirmarDelete = () => {
    onDelete(selectedFields);
    setShowConfirmacion(false);
  };
  
  // Obtener las etiquetas personalizadas de los campos
  const camposTextoLabels = [
    { campo: 'texto1', label: empresa?.ntcliente1 || 'Texto 1' },
    { campo: 'texto2', label: empresa?.ntcliente2 || 'Texto 2' },
    { campo: 'texto3', label: empresa?.ntcliente3 || 'Texto 3' },
    { campo: 'texto4', label: empresa?.ntcliente4 || 'Texto 4' },
    { campo: 'texto5', label: empresa?.ntcliente5 || 'Texto 5' },
  ];
  
  // Obtener los nombres de los campos seleccionados
  const camposSeleccionadosNombres = selectedFields
    .map(campo => camposTextoLabels.find(c => c.campo === campo)?.label)
    .filter(Boolean);
  
  return (
    <>
      {showConfirmacion ? (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-destructive">¿Confirmar borrado?</h3>
          <p className="text-muted-foreground">
            Esta acción eliminará los siguientes campos de todos los clientes:
          </p>
          <ul className="list-disc pl-5 my-2">
            {camposSeleccionadosNombres.map((nombre, index) => (
              <li key={index} className="font-medium">{nombre}</li>
            ))}
          </ul>
          <p className="text-destructive font-semibold">Esta acción no se puede deshacer.</p>
          
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={() => setShowConfirmacion(false)}
              className="px-4 py-2 border border-border rounded-md"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmarDelete}
              className="bg-destructive text-white px-4 py-2 rounded-md"
            >
              Confirmar borrado
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            {camposTextoLabels.map(({ campo, label }) => (
              <label key={campo} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedFields.includes(campo)}
                  onChange={() => toggleField(campo)}
                  className="rounded text-primary focus:ring-primary"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-border rounded-md"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={selectedFields.length === 0}
              className="bg-destructive text-white px-4 py-2 rounded-md disabled:opacity-50"
            >
              Borrar campos
            </button>
          </div>
        </form>
      )}
    </>
  );
};

export default ClientePage; 